import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Button } from '$partials/ui';
import { useLanguage } from '$contexts/language.jsx';
import { StaticTitle } from '$partials/layout/side-titles.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ContactGlobalEnquiry = ({
    emailRecipient = null,
    facebook       = null,
    instagram      = null,
    linkedin       = null,
    sideTitle      = null,
    subjects       = null,
    title          = null,
    twitter        = null,
    wechat         = null,
    weibo          = null,
    youtube        = null,
}) => {
    // hooks
    const { translate: t } = useLanguage();

    const {
        formState: { errors },
        handleSubmit,
        register
    } = useForm({
        shouldFocusError: true,
        shouldUnregister: true
    });

    // state
    const [ submitResponse, setSubmitResponse ] = useState();

    // methods
    const handleSubmitInternal = async (data) => {
        try {
            const response = await fetch('/api/contact/v1/general-enquiry', {
                body: JSON.stringify({
                    'email_recipient': data.email_recipient,
                    'email_sender'   : data.email_sender,
                    'message'        : data.message,
                    'name'           : data.name,
                    'subject'        : data.subject,
                }),
                headers: {
                    'Content-type': 'application/json',
                },
                method: 'POST'
            });

            // convert response to json
            const result = await response.json();

            // if original request fails
            if (response.status !== 200) {
                throw result;
            }

            // if server request fails
            if (result.status === 400) {
                throw result;
            }

            // if request works
            setSubmitResponse(t('Thank you, we have now received your message'));
        } catch (err) {
            if (err.message || err.title) {
                setSubmitResponse(err.message || t('Sorry something went wrong, please try again later'));
            } else {
                setSubmitResponse(t('Sorry something went wrong, please try again later'));
            }
        }
    };

    // render
    return (
        <section className='module-contact-global-enquiry'>
            <div className='pr-gutter mxn-gutter'>  {/* outer grid gutter */}
                <div className='flex flex-wrap mb7 sm-mb7 md-mb9'>
                    {/* side title */}
                    <div className='absolute md-relative md-col-2'>
                        {(title && sideTitle) && (
                            <div className='px-gutter'>
                                <p className='type-style-5 side-title'>
                                    <StaticTitle title={sideTitle} />
                                </p>
                            </div>
                        )}
                    </div>

                    {/* content / title */}
                    <div className='col-11 sm-col-8 md-col-7'>
                        <div className='ml9 sm-ml9 md-m0'>
                            <div className='px-gutter'>
                                <div
                                    className='rich-text type-style-1'
                                    dangerouslySetInnerHTML={{ __html: title }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* content */}
            <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                <div className='flex flex-wrap'>
                    {/* side title */}
                    <div className='absolute md-relative md-col-2'>
                        {(!title && sideTitle) && (
                            <div className='px-gutter'>
                                <p className='type-style-5 side-title side-title--lesser-top'>
                                    <StaticTitle title={sideTitle} />
                                </p>
                            </div>
                        )}
                    </div>

                    {/* content */}
                    <div className='col-12 md-col-10'>
                        <div className='ml9 sm-ml9 md-m0'>
                            <div className='flex flex-wrap'>
                                {/* hide until client has mail service to use */}
                                <div className='col-12 sm-col-6 md-col-6'>
                                    <div className='px-gutter'>
                                        <h4 className='type-style-3 pb3 border-bottom border-black capitalise'>
                                            {t('Message Us')}:
                                        </h4>

                                        <form
                                            action='/api/contact/v1/general-enquiry'
                                            method='post'
                                            onSubmit={handleSubmit(handleSubmitInternal)}>
                                            {/* email recipient */}
                                            <input
                                                {...register('email_recipient', { required: t('Recipient email address is missing') })}
                                                name='email_recipient'
                                                type='hidden'
                                                value={emailRecipient} />

                                            {/* name */}
                                            <div className='flex items-center justify-between border-bottom border-black'>
                                                <label
                                                    className='accessible-hide type-style-6 capitalise'
                                                    htmlFor='frm-name'>
                                                    {t('Name')}
                                                </label>
                                                <input
                                                    {...register('name', { required: t('Please enter your name') })}
                                                    required
                                                    className='form-field-reset width-100 pb3 pt4 type-style-6'
                                                    id='frm-name'
                                                    label={t('Name')}
                                                    maxLength={100}
                                                    placeholder={t('Enter your name')}
                                                    type='text' />
                                            </div>

                                            {/* email sender */}
                                            <div className='flex items-center justify-between border-bottom border-black'>
                                                <label
                                                    className='accessible-hide type-style-6 capitalise'
                                                    htmlFor='frm-email-sender'>
                                                    {t('Email')}
                                                </label>
                                                <input
                                                    {...register('email_sender', { required: t('Please enter your email') })}
                                                    required
                                                    className='form-field-reset width-100 pb3 pt4 type-style-6'
                                                    id='frm-email-sender'
                                                    label={t('Email')}
                                                    maxLength={100}
                                                    placeholder={`... ${t('your email')}`}
                                                    type='email' />
                                            </div>

                                            {/* subject */}
                                            <div className='flex items-center justify-between border-bottom border-black black'>
                                                <label
                                                    className='accessible-hide type-style-6 capitalise'
                                                    htmlFor='frm-subject'>
                                                    {t('Subject')}
                                                </label>
                                                <select
                                                    {...register('subject', { required: t('Please choose a subject') })}
                                                    required
                                                    className='form-field-reset width-100 pb3 pt4 type-style-6'
                                                    id='frm-subject'
                                                    label={t('Subject')}>
                                                    <option value=''>... a subject</option>
                                                    {subjects?.map((subject, idx) => {
                                                        return (
                                                            <option
                                                                key={idx}
                                                                value={subject.emailSubject}>{subject.label}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>

                                            {/* message */}
                                            <div className='flex items-center justify-between border-bottom border-black'>
                                                <label
                                                    className='accessible-hide type-style-6 capitalise'
                                                    htmlFor='frm-message'>
                                                    {t('Message')}
                                                </label>
                                                <textarea
                                                    {...register('message', { required: t('Please enter your message') })}
                                                    required
                                                    className='form-field-reset width-100 pb3 pt4 type-style-6'
                                                    id='frm-message'
                                                    label={t('Message')}
                                                    maxLength={1000}
                                                    placeholder={`... ${t('your message')}`}
                                                    style={{ resize: 'none' }} />
                                            </div>

                                            {/* submit */}
                                            <div className='mt5'>
                                                <Button
                                                    className='button'
                                                    type='submit'>
                                                    <span className='capitalise'>
                                                        {t('Send')}
                                                    </span>
                                                </Button>
                                            </div>

                                            {/* errors */}
                                            <div className='type-style-6 italic red-orange mt5'>
                                                {errors?.name?.message && (
                                                    <p>
                                                        {errors.name.message}
                                                    </p>
                                                )}

                                                {errors?.email?.message && (
                                                    <p>
                                                        {errors.email.message}
                                                    </p>
                                                )}

                                                {errors?.subject?.message && (
                                                    <p>
                                                        {errors.subject.message}
                                                    </p>
                                                )}

                                                {errors?.message?.message && (
                                                    <p>
                                                        {errors.message.message}
                                                    </p>
                                                )}
                                            </div>
                                        </form>

                                        {/* submit response */}
                                        {submitResponse && (
                                            <div
                                                className='rich-text my5'
                                                dangerouslySetInnerHTML={{ __html: submitResponse }} />
                                        )}
                                    </div>
                                </div>
                                <div className='col-12 sm-col-6 md-col-6 mt7 sm-mt0 md-mt0'>
                                    <div className='px-gutter'>
                                        <h4 className='type-style-3 pb3 border-bottom border-black capitalise'>
                                            {t('Follow Us')}:
                                        </h4>

                                        <ul className='list-reset'>
                                            {facebook && (
                                                <li className='border-bottom border-black'>
                                                    <a
                                                        className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                        href={facebook}
                                                        rel='noreferrer noopener'
                                                        target='_blank'>
                                                        Facebook
                                                    </a>
                                                </li>
                                            )}

                                            {instagram && (
                                                <li className='border-bottom border-black'>
                                                    <a
                                                        className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                        href={instagram}
                                                        rel='noreferrer noopener'
                                                        target='_blank'>
                                                        Instagram
                                                    </a>
                                                </li>
                                            )}

                                            {linkedin && (
                                                <li className='border-bottom border-black'>
                                                    <a
                                                        className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                        href={linkedin}
                                                        rel='noreferrer noopener'
                                                        target='_blank'>
                                                        LinkedIn
                                                    </a>
                                                </li>
                                            )}

                                            {twitter && (
                                                <li className='border-bottom border-black'>
                                                    <a
                                                        className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                        href={twitter}
                                                        rel='noreferrer noopener'
                                                        target='_blank'>
                                                        Twitter
                                                    </a>
                                                </li>
                                            )}

                                            {wechat && (
                                                <li className='border-bottom border-black'>
                                                    <a
                                                        className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                        href={wechat}
                                                        rel='noreferrer noopener'
                                                        target='_blank'>
                                                        WeChat
                                                    </a>
                                                </li>
                                            )}

                                            {weibo && (
                                                <li className='border-bottom border-black'>
                                                    <a
                                                        className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                        href={weibo}
                                                        rel='noreferrer noopener'
                                                        target='_blank'>
                                                        Weibo
                                                    </a>
                                                </li>
                                            )}

                                            {youtube && (
                                                <li className='border-bottom border-black'>
                                                    <a
                                                        className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                        href={youtube}
                                                        rel='noreferrer noopener'
                                                        target='_blank'>
                                                        YouTube
                                                    </a>
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    emailRecipient: PropTypes.string.isRequired,
    facebook      : PropTypes.string,
    instagram     : PropTypes.string,
    linkedin      : PropTypes.string,
    sideTitle     : PropTypes.string,
    subjects      : PropTypes.array.isRequired,
    title         : PropTypes.string,
    twitter       : PropTypes.string,
    wechat        : PropTypes.string,
    weibo         : PropTypes.string,
    youtube       : PropTypes.string,
};

ContactGlobalEnquiry.propTypes = propTypes;

export { ContactGlobalEnquiry };

export default ContactGlobalEnquiry;
